import React from 'react';
import { Modal, ConfigProvider, Tag, Typography, Divider } from 'antd';
import locale from "antd/es/locale/es_ES";
import { TrophyOutlined, FileTextOutlined, BulbOutlined, ExperimentOutlined } from '@ant-design/icons';
import ProblemaIdEnunciat from "../../problemesId/content/ProblemaIdEnunciat";
import ProblemaIdExemples from "../../problemesId/content/ProblemaIdExemples";
import JutgeColors from "../../../../styles/JutgeColors";
import ModoDesafioTag from "./ModoDesafioTag";

const { Title } = Typography;

interface Props {
    element: any;
    id_activitat: string;
    visible: boolean;
    onClose: () => void;
}

// Colors segons dificultat
const dificultatColor = (d?: string) => {
    const v = (d || '').toLowerCase();
    if (v.includes('begin') || v.includes('facil') || v.includes('fàcil') || v.includes('easy')) return 'green';
    if (v.includes('inter') || v.includes('mig') || v.includes('medium')) return 'gold';
    if (v.includes('avan') || v.includes('hard') || v.includes('dificil') || v.includes('difícil')) return 'red';
    return 'blue';
};

const EnunciatModal: React.FC<Props> = (props: Props) => {
    const ele = props.element || {};
    const tags: string[] = ele?.tags ? String(ele.tags).split(' ').filter((t: string) => t) : [];
    const nom = ele.prac || ele.nom;

    return (
        <ConfigProvider locale={locale}>
            <Modal
                open={props.visible}
                onCancel={props.onClose}
                footer={null}
                width={920}
                style={{ top: 24 }}
                styles={{
                    content: { padding: 0, borderRadius: 16, overflow: 'hidden' },
                    body: { padding: 0 }
                }}
                closeIcon={<span style={{ color: '#fff', fontSize: 18 }}>✕</span>}
            >
                {/* Capçalera amb degradat */}
                <div
                    style={{
                        background: `linear-gradient(135deg, ${JutgeColors.colors.color5} 0%, ${JutgeColors.colors.color3} 100%)`,
                        color: '#fff',
                        padding: '24px 28px 20px',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: 0.9, fontSize: 13, marginBottom: 6 }}>
                        <FileTextOutlined />
                        <span style={{ textTransform: 'uppercase', letterSpacing: 1 }}>Enunciat de l'exercici</span>
                    </div>
                    <Title level={3} style={{ color: '#fff', margin: 0 }}>{nom}</Title>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14, alignItems: 'center' }}>
                        {ele.dificultat && (
                            <Tag color={dificultatColor(ele.dificultat)} style={{ borderRadius: 20, padding: '2px 10px', margin: 0, fontWeight: 600 }}>
                                {ele.dificultat}
                            </Tag>
                        )}
                        {ele.puntuacion > 1 && (
                            <Tag color="gold" style={{ borderRadius: 20, padding: '2px 10px', margin: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <TrophyOutlined /> {ele.puntuacion} punts
                            </Tag>
                        )}
                        {typeof ele.tests !== 'undefined' && (
                            <Tag color="blue" style={{ borderRadius: 20, padding: '2px 10px', margin: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <ExperimentOutlined /> {ele.tests} tests
                            </Tag>
                        )}
                        {ele.progres === 1 && <ModoDesafioTag origenSuccess={ele.origen_success} />}
                        {tags.map((t, i) => (
                            <Tag key={i} style={{ borderRadius: 20, padding: '2px 10px', margin: 0, background: 'rgba(255,255,255,0.18)', color: '#fff', border: 'none' }}>
                                {t}
                            </Tag>
                        ))}
                    </div>
                </div>

                {/* Amaguem el títol duplicat que renderitza ProblemaIdEnunciat i millorem l'espaiat */}
                <style>{`
                    .enunciat-modal-body > h3:first-child { display: none; }
                    .enunciat-modal-body > br:first-of-type { display: none; }
                    .enunciat-modal-body hr { border: none; border-top: 1px solid #eef0f2; margin: 16px 0; }
                    .enunciat-modal-body p { line-height: 1.6; }
                    .enunciat-modal-body pre { background: #f5f5f5; padding: 12px; border-radius: 8px; overflow: auto; }
                    .enunciat-modal-body code { background: #eef0f2; padding: 1px 5px; border-radius: 4px; }
                    .enunciat-modal-body pre code { background: transparent; padding: 0; }
                `}</style>

                {/* Cos */}
                <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '24px 28px', background: '#fff' }}>
                    {props.element && (
                        <>
                            <div
                                style={{
                                    background: '#fafbfc',
                                    border: '1px solid #eef0f2',
                                    borderRadius: 12,
                                    padding: '18px 22px',
                                }}
                                className="enunciat-modal-body"
                            >
                                <ProblemaIdEnunciat
                                    propIdProblema={props.element.id?.toString()}
                                    propIdActivitat={props.id_activitat}
                                    readOnly={true}
                                />
                            </div>

                            <Divider orientation="left" style={{ marginTop: 28 }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: JutgeColors.colors.color5 }}>
                                    <BulbOutlined /> Exemples
                                </span>
                            </Divider>

                            <ProblemaIdExemples
                                propIdProblema={props.element.id?.toString()}
                                propIdActivitat={props.id_activitat}
                            />
                        </>
                    )}
                </div>
            </Modal>
        </ConfigProvider>
    );
};

export default EnunciatModal;
